// src/appRWAPromo/components/UnifiedMarketCard.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";

import { Box, Card, Chip, Typography, Avatar, IconButton, Tooltip, Button, alpha } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";

import VisibilityIcon from "@mui/icons-material/Visibility";
import ShareIcon from "@mui/icons-material/Share";
import BoltIcon from "@mui/icons-material/Bolt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";

import { useInViewport } from "../hooks/useInViewport";

const PLACEHOLDER_800 = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#0b1220"/>
      <stop offset="1" stop-color="#050913"/>
    </linearGradient>
  </defs>
  <rect width="100%" height="100%" fill="url(#g)"/>
</svg>
`)}`;

const PLACEHOLDER_96 = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#0b1220"/>
      <stop offset="1" stop-color="#050913"/>
    </linearGradient>
  </defs>
  <rect width="100%" height="100%" rx="14" ry="14" fill="url(#g)"/>
</svg>
`)}`;

const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.92) 0%, rgba(10,13,17,.92) 100%)",
  border: "1px solid rgba(255,255,255,.08)",
  shadow: "0 10px 32px rgba(2,8,23,.45)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const StyledCard = styled(Card)(() => ({
  position: "relative",
  borderRadius: 10,
  width: "100%",
  minWidth: 0,
  height: "100%",               // <= добавили
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  border: GLASS.border,
  background: GLASS.bg,
  color: GLASS.text,
  boxShadow: GLASS.shadow,
  transition: "transform .2s, box-shadow .2s, border-color .2s, filter .2s",
  "&:hover": {
    transform: "translateY(-4px)",
    borderColor: "rgba(255,255,255,.18)",
    boxShadow: `0 16px 40px rgba(2,8,23,.55), 0 0 36px rgba(56,189,248,.22)`,
    filter: "saturate(1.05)",
  },
}));


const TopChips = styled(Box)(() => ({
  position: "absolute",
  top: 8,
  left: 8,
  right: 8,
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
  zIndex: 5,
}));

const MediaWrap = styled(Box)(() => ({
  position: "relative",
  width: "100%",
  aspectRatio: "4 / 3",
  background: "rgba(255,255,255,.04)",
  borderBottom: "1px solid rgba(255,255,255,.08)",
  overflow: "hidden",
}));

const MediaOverlayBottom = styled(Box)(() => ({
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  background:
    "linear-gradient(180deg, rgba(0,0,0,.25) 0%, rgba(0,0,0,0) 45%, rgba(0,0,0,.55) 100%)",
}));

const Row = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
}));

const fmt = (n) => {
  const x = Number(n || 0);
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(1)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}K`;
  return String(x);
};

async function shareLink(url, title) {
  if (!url) return;
  try {
    if (typeof navigator !== "undefined" && navigator.share) {
      await navigator.share({ title: title || "Market", url });
      return;
    }
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return;
    }
  } catch {}
  try {
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {}
}

function truthy(v) {
  return v === true || v === "true" || v === 1;
}

export default function UnifiedMarketCard({
  item,
  onOpen,
  onPrimary,
  onTips,
  onBoost,
  onYes,
  onNo,
  compact = false,
  priority = false,
  skeleton = false,
}) {
  const type = String(item?.type || item?.kind || "").toLowerCase();
  const isStream = type === "stream";
  const isPrediction = type === "prediction";

  const yesBets = Number(item?.yesBets ?? 0);
  const noBets = Number(item?.noBets ?? 0);

  const hasBets = yesBets + noBets > 0;
  const totalBets = hasBets ? yesBets + noBets : 1;

  const yesPct = hasBets ? Math.round((yesBets / totalBets) * 100) : 50;
  const noPct = 100 - yesPct;

  const leadingSide = !hasBets ? "tie" : yesBets === noBets ? "tie" : yesBets > noBets ? "yes" : "no";

  const title = item?.name || item?.title || "Market";
  const subtitle = item?.handle || item?.subtitle || "";
  const avatarUrl = item?.avatarUrl || "";

  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);

  const poster = item?.poster || "";
  const posterLow = item?.posterLow || item?.posterBlur || "";
  const imageSrcLow = item?.imageLow || "";

  const mediaSrc = isStream ? (item?.video480 || item?.videoSrc || "") : (item?.imageSrc || "");
  const previewSrc = isStream ? (item?.preview480 || item?.previewSrc || "") : "";

  // ✅ основной фон
  const fallbackImage = isStream
    ? (posterLow || poster || PLACEHOLDER_800)
    : (imageSrcLow || posterLow || poster || mediaSrc || PLACEHOLDER_800);

  // ✅ FIX: prediction thumb сначала poster (а не imageSrc)
  const thumbSrc = isStream
    ? (posterLow || poster || PLACEHOLDER_96)
    : (imageSrcLow || posterLow || poster || mediaSrc || PLACEHOLDER_96);

  // ✅ progressive low -> hi (без пустого кадра)
  const lowImg = isStream ? (posterLow || "") : (imageSrcLow || posterLow || "");
  const hiImg = isStream ? (poster || "") : (poster || item?.imageSrc || "");

  const initialImg = lowImg || hiImg || PLACEHOLDER_800;

  const [displayImg, setDisplayImg] = useState(initialImg);
  const [isHiRes, setIsHiRes] = useState(Boolean(!lowImg && hiImg));

  useEffect(() => {
    const init = lowImg || hiImg || PLACEHOLDER_800;
    setDisplayImg(init);
    setIsHiRes(Boolean(!lowImg && hiImg));

    if (hiImg && hiImg !== lowImg) {
      const img = new Image();
      img.decoding = "async";
      img.src = hiImg;
      img.onload = () => {
        setDisplayImg(hiImg);
        setIsHiRes(true);
      };
    }
  }, [item?.id, lowImg, hiImg]);



  const flags = item?.flags || {};
  const isNew = truthy(flags.isNew) || truthy(item?.isNew);
  const isHot = truthy(flags.isHot) || truthy(item?.isHot);
  const isBreaking = truthy(flags.isBreaking) || truthy(item?.isBreaking);

  const category = String(item?.category || item?.market || "").trim();

  const status = String(item?.status || (isStream ? "record" : "approved")).toLowerCase();

  // ✅ priority карточки “предзагружаем” раньше (видео стартует до того как видно)
  const { ref: mediaRef, inView } = useInViewport({
    rootMargin: priority ? "1400px 0px" : "400px 0px",
    threshold: 0.1,
  });

  const hasPreview = Boolean(previewSrc);
  const showPreviewVideo = isStream && hasPreview && inView && !skeleton;
  const showMainVideo = isStream && !hasPreview && Boolean(mediaSrc) && inView && !skeleton;

  useEffect(() => {
    setVideoReady(false);
  }, [previewSrc, mediaSrc]);

useEffect(() => {
  if (!inView) return;
  if (!(showPreviewVideo || showMainVideo)) return;

  const v = videoRef.current;
  if (!v) return;

  // гарантируем попытку старта, даже если autoplay "задумался"
  v.play().catch(() => {});
}, [inView, showPreviewVideo, showMainVideo, previewSrc, mediaSrc]);



  const statusChipSx = useMemo(() => {
    const common = {
      height: compact ? 18 : 20,
      fontWeight: 900,
      fontSize: compact ? 10 : 11,
      "& .MuiChip-label": { px: compact ? 0.6 : 0.85 },
    };

    if (status === "live") {
      return {
        ...common,
        bgcolor: "rgba(16,185,129,.16)",
        color: "#34d399",
        border: "1px solid rgba(16,185,129,.38)",
      };
    }

    return {
      ...common,
      bgcolor: "rgba(148,163,184,.12)",
      color: "#cbd5e1",
      border: "1px solid rgba(148,163,184,.25)",
    };
  }, [compact, status]);

  const motionProps = priority
    ? { initial: false, animate: { opacity: 1, y: 0 } }
    : { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 } };

  const isSvgPlaceholder = typeof displayImg === "string" && displayImg.startsWith("data:image/svg+xml");
  const imgFilter = isHiRes || isSvgPlaceholder ? "none" : "blur(10px)";
  const imgTransform = isHiRes || isSvgPlaceholder ? "scale(1)" : "scale(1.04)";

  // =======================
  // PREDICTION CARD
  // =======================
  if (isPrediction) {
    const yesPrice = Number(item?.yesPrice ?? item?.priceYes ?? NaN);
const noPrice = Number(item?.noPrice ?? item?.priceNo ?? (Number.isFinite(yesPrice) ? 1 - yesPrice : NaN));

const hasPrices = Number.isFinite(yesPrice) && Number.isFinite(noPrice) && yesPrice > 0 && noPrice > 0;

const impliedYesPct = hasPrices ? Math.round(yesPrice * 100) : yesPct;
const impliedNoPct  = hasPrices ? Math.round(noPrice * 100)  : noPct;

    return (
      <motion.div transition={{ duration: 0.22 }} style={{ width: "100%" }} {...motionProps}>
        <StyledCard>
          <Box
            onClick={() => (skeleton ? null : onOpen?.(item))}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (skeleton) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen?.(item);
              }
            }}
            sx={{ cursor: skeleton ? "default" : "pointer", p: 1.2 }}
          >
            <Box sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}>
              <Box
                component="img"
                src={thumbSrc || PLACEHOLDER_96}
                alt={item?.title || item?.name || "thumb"}
                loading={priority ? "eager" : "lazy"}
                fetchpriority={priority ? "high" : "auto"}
                decoding="async"
                onError={(e) => (e.currentTarget.src = PLACEHOLDER_96)}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  objectFit: "cover",
                  flex: "0 0 auto",
                  border: "1px solid rgba(255,255,255,.10)",
                  bgcolor: "rgba(255,255,255,.06)",
                }}
              />

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 950,
                    color: GLASS.text,
                    fontSize: 13,
                    lineHeight: 1.2,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {item?.name || item?.title || "Market"}
                </Typography>

                {!!(item?.handle || item?.subtitle) && (
                  <Typography noWrap sx={{ mt: 0.2, color: GLASS.subtext, fontSize: 11 }}>
                    {item?.handle || item?.subtitle}
                  </Typography>
                )}

                <Box
                  sx={{
                    mt: 0.9,
                    height: 6,
                    borderRadius: 999,
                    overflow: "hidden",
                    bgcolor: "rgba(148,163,184,.18)",
                  }}
                >
                  <Box sx={{ width: `${yesPct}%`, height: "100%", bgcolor: "rgba(96,165,250,.90)" }} />
                </Box>

                <Box sx={{ mt: 0.5, display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 11, color: GLASS.subtext }}>Yes {yesPct}%</Typography>
                  <Typography sx={{ fontSize: 11, color: GLASS.subtext }}>No {noPct}%</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 1.1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              <Button
                disableElevation
                disableRipple
                onClick={(e) => {
                  e.stopPropagation();
                  onYes?.(item);
                }}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 900,
                  borderRadius: 2,
                  py: 1.05,
                  color: "#e5e7eb",
                  boxShadow: "none",
                  border: "1px solid rgba(255,255,255,.14)",
                  bgcolor: leadingSide === "yes" ? "rgba(96,165,250,.92)" : "rgba(96,165,250,.26)",
                  "&:hover": {
                    bgcolor: leadingSide === "yes" ? "rgba(96,165,250,.92)" : "rgba(96,165,250,.26)",
                    filter: "brightness(.92)",
                    boxShadow: "none",
                  },
                  "&:active": { transform: "translateY(1px)" },
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <Typography sx={{ fontWeight: 950 }}>Yes</Typography>
                    <Typography sx={{ fontWeight: 950 }}>{impliedYesPct}%</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11, opacity: 0.85 }}>
                    {hasBets ? `${fmt(yesBets)} bets` : "—"}
                  </Typography>
                </Box>
              </Button>

              <Button
                disableElevation
                disableRipple
                onClick={(e) => {
                  e.stopPropagation();
                  onNo?.(item);
                }}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 900,
                  borderRadius: 2,
                  py: 1.05,
                  color: "#e5e7eb",
                  boxShadow: "none",
                  border: "1px solid rgba(255,255,255,.14)",
                  bgcolor: leadingSide === "no" ? "rgba(216,180,254,.92)" : "rgba(216,180,254,.26)",
                  "&:hover": {
                    bgcolor: leadingSide === "no" ? "rgba(216,180,254,.92)" : "rgba(216,180,254,.26)",
                    filter: "brightness(.92)",
                    boxShadow: "none",
                  },
                  "&:active": { transform: "translateY(1px)" },
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <Typography sx={{ fontWeight: 950 }}>No</Typography>
                    <Typography sx={{ fontWeight: 950 }}>{impliedNoPct}%</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11, opacity: 0.85 }}>
                    {hasBets ? `${fmt(noBets)} bets` : "—"}
                  </Typography>
                </Box>
              </Button>
            </Box>

            <Typography sx={{ mt: 0.8, fontSize: 11, color: GLASS.subtext }}>
              {leadingSide === "tie" ? "Even bets" : leadingSide === "yes" ? "More bets on Yes" : "More bets on No"}
            </Typography>
          </Box>
        </StyledCard>
      </motion.div>
    );
  }

  // =======================
  // STREAM CARD
  // =======================
  const categoryChipSx = {
    height: compact ? 18 : 20,
    bgcolor: "rgba(30,64,175,.45)",
    border: "1px solid rgba(59,130,246,.65)",
    color: "#bfdbfe",
    fontWeight: 900,
    fontSize: compact ? 10 : 11,
    "& .MuiChip-label": { px: compact ? 0.6 : 0.85 },
  };

  const flagChipSx = (tone) => ({
    height: compact ? 18 : 20,
    fontWeight: 950,
    fontSize: compact ? 10 : 11,
    "& .MuiChip-label": { px: compact ? 0.6 : 0.85 },
    ...(tone === "hot"
      ? { bgcolor: "rgba(244,63,94,.14)", color: "#fda4af", border: "1px solid rgba(244,63,94,.30)" }
      : tone === "breaking"
      ? { bgcolor: "rgba(251,191,36,.12)", color: "#fde68a", border: "1px solid rgba(251,191,36,.28)" }
      : { bgcolor: "rgba(56,189,248,.14)", color: "#bae6fd", border: "1px solid rgba(56,189,248,.30)" }),
  });

  const handleOpen = () => onOpen?.(item);

  const primaryLabel = "Get analysis";
  const PrimaryIcon = AutoAwesomeIcon;

  return (
    <motion.div transition={{ duration: 0.28 }} {...motionProps}>
      <StyledCard>
        <TopChips>
          <Chip label={status === "live" ? "LIVE" : "RECORD"} sx={statusChipSx} />
          {isNew ? <Chip label="NEW" sx={flagChipSx("new")} /> : null}
          {isHot ? <Chip label="HOT" sx={flagChipSx("hot")} /> : null}
          {isBreaking ? <Chip label="BREAKING" sx={flagChipSx("breaking")} /> : null}
          {category ? <Chip label={category.toUpperCase()} sx={categoryChipSx} /> : null}
        </TopChips>

        <Box
          onClick={skeleton ? undefined : handleOpen}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (skeleton) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleOpen();
            }
          }}
          sx={{ cursor: skeleton ? "default" : "pointer" }}
        >
          <MediaWrap ref={mediaRef}>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: lowImg
                  ? `url(${lowImg}) center/cover no-repeat`
                  : "linear-gradient(135deg, rgba(255,255,255,.06), rgba(255,255,255,.02))",
                filter: lowImg ? "blur(18px)" : "none",
                transform: lowImg ? "scale(1.08)" : "none",
                opacity: 0.95,
              }}
            />

            <Box
              component="img"
              src={displayImg || fallbackImage}
              alt={title || "media"}
              loading={priority ? "eager" : "lazy"}
              fetchpriority={priority ? "high" : "auto"}
              decoding="async"
              onError={(e) => {
                e.currentTarget.src = PLACEHOLDER_800;
              }}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                filter: imgFilter,
                transform: imgTransform,
                transition: "filter .18s ease-out, transform .18s ease-out",
              }}
            />

            {isStream && (showPreviewVideo || showMainVideo) && (
  <Box
    component="video"
    ref={videoRef}
    src={showPreviewVideo ? previewSrc : mediaSrc}
    poster={displayImg || fallbackImage}
    muted
    playsInline
    autoPlay
    loop={Boolean(item?.loop ?? true)}
    controls={false}
    disablePictureInPicture
    controlsList="nodownload noplaybackrate noremoteplayback"
    // КЛЮЧЕВОЕ: preload по режиму, а не по priority
    preload={showPreviewVideo ? "auto" : (priority ? "metadata" : "none")}
    onLoadedData={() => setVideoReady(true)}
    onCanPlay={() => setVideoReady(true)}
    onPlaying={() => setVideoReady(true)}
    onPause={() => {
      const v = videoRef.current;
      if (v) v.play().catch(() => {});
    }}
    onError={() => setVideoReady(false)}
    style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
      opacity: videoReady ? 1 : 0,
      transition: "opacity .18s ease-out",
      pointerEvents: "none",      // чтобы не было паузы кликом
      background: "transparent",  // минимизирует black flash на некоторых браузерах
    }}
  />
)}


            <MediaOverlayBottom>
              <Box sx={{ p: 1.1, display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  src={avatarUrl}
                  sx={{
                    width: compact ? 26 : 30,
                    height: compact ? 26 : 30,
                    bgcolor: alpha("#94a3b8", 0.25),
                    fontWeight: 900,
                    border: "1px solid rgba(255,255,255,.12)",
                  }}
                >
                  {(title || "?").charAt(0)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 900, color: "#e5e7eb", fontSize: compact ? 12 : 13, lineHeight: 1.05 }}>
                    {title}
                  </Typography>
                  <Typography noWrap sx={{ color: "#cbd5e1", fontSize: compact ? 10 : 11, opacity: 0.9 }}>
                    {subtitle}
                  </Typography>
                </Box>
              </Box>
            </MediaOverlayBottom>
          </MediaWrap>

          <Box sx={{ p: compact ? 1.1 : 1.4, display: "flex", flexDirection: "column", gap: 0.8 }}>
            <Typography sx={{ fontWeight: 950, color: GLASS.text, fontSize: compact ? 13 : 14, lineHeight: 1.15 }}>
              {title}
            </Typography>

            <Typography
              sx={{
                color: GLASS.subtext,
                fontSize: compact ? 11 : 12,
                lineHeight: 1.25,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item?.description || ""}
            </Typography>

            <Row sx={{ mt: 0.3 }}>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onPrimary?.(item);
                }}
                startIcon={<PrimaryIcon />}
                size={compact ? "small" : "medium"}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontWeight: 900,
                  borderRadius: 2,
                  flex: 1,
                  background: "rgba(56,189,248,.95)",
                  color: "#04131f",
                  "&:hover": { background: "rgba(56,189,248,1)" },
                }}
              >
                {primaryLabel}
              </Button>

              <Tooltip title="Tips">
                <span>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onTips?.(item);
                    }}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      border: "1px solid rgba(255,255,255,.12)",
                      bgcolor: "rgba(16,185,129,.10)",
                      "&:hover": { bgcolor: "rgba(16,185,129,.16)" },
                    }}
                  >
                    <MonetizationOnIcon sx={{ color: "#a7f3d0" }} />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Boost">
                <span>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onBoost?.(item);
                    }}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      border: "1px solid rgba(255,255,255,.12)",
                      bgcolor: "rgba(20,241,149,.10)",
                      "&:hover": { bgcolor: "rgba(20,241,149,.16)" },
                    }}
                  >
                    <BoltIcon sx={{ color: "#bfffe5" }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Row>
          </Box>
        </Box>

        <Box
          sx={{
            px: compact ? 1 : 1.4,
            py: 0.7,
            borderTop: "1px solid rgba(255,255,255,.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <VisibilityIcon sx={{ color: "#cbd5e1", fontSize: 18 }} />
            <Typography sx={{ color: "#e5e7eb", fontWeight: 800, fontSize: 12 }}>
              {fmt(item?.views || 0)}
            </Typography>
          </Box>

          <Tooltip title="Share">
            <span>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  shareLink(item?.shareUrl, title);
                }}
                size="small"
              >
                <ShareIcon sx={{ color: "#cbd5e1", fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </StyledCard>
    </motion.div>
  );
}
